// src/utils/compOverviewCredits.js
// Admin Comp Overview: per-recipient credit lists and the memo lines under the
// net (STEP3_5A_TIERS_DESIGN.md sections 4.5 and 5.15; release R2).
//
// Display only. Every figure is the server's (adminGetCompOverview
// months[].memberships / months[].directByRecipient and the yearTotals of the
// same shape); nothing is recomputed here. Zero suppression: a memo line shows
// only when its value is > 0 (M1 when revenue or credits are non-zero, M9 when
// membership revenue is > 0).
//
// Server compatibility: an older server sends no memberships /
// directByRecipient key, so every function returns [] or null and the screen
// renders nothing (never $0.00 for a figure the server did not compute).
//
// Source stays ASCII: the minus is '\u2212' and the middle dot '\u00b7'.
// Zero imports: unit-tested in plain node
// (web-landing/scripts/test-comp-overview-credits.mjs).

const MINUS = '\u2212';
const DOT = ' \u00b7 ';

const isCents = (v) => typeof v === 'number' && Number.isFinite(v);
const pos = (v) => isCents(v) && v > 0;
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// The screen's sign-aware fmt$ logic: "-$123.45", never "$-123.45".
export function fmtUsd(cents) {
  const v = (cents || 0) / 100;
  return `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`;
}

// Tier display names. The four tier keys equal constants/membershipTiers.js
// TIER_KEYS (pinned by the tiers harness); 'untiered' is a credit with no
// stored meta.tierKey, 'team' a team membership credit.
export const TIER_LABELS = {
  spark: 'Spark',
  amped: 'Amped',
  overdrive: 'Overdrive',
  maxed: 'Maxed',
  untiered: 'No tier recorded',
  team: 'Team memberships',
};
export const TIER_ORDER = ['spark', 'amped', 'overdrive', 'maxed', 'untiered', 'team'];
const CHANNEL_TIERS = ['spark', 'amped', 'overdrive', 'maxed', 'untiered'];

const paymentsText = (n) => plural(n, 'payment', 'payments');

// One "Top creators: membership credits" row's detail line:
// "Spark $3.19 \u00b7 Amped $6.69 \u00b7 ... \u00b7 Team $x \u00b7 4 payments" (non-zero parts only).
export function membershipDetailText(row) {
  if (!row || typeof row !== 'object') return null;
  const byTier = row.byTier && typeof row.byTier === 'object' ? row.byTier : {};
  const parts = [];
  for (const t of CHANNEL_TIERS) {
    if (pos(byTier[t])) parts.push(`${TIER_LABELS[t]} ${fmtUsd(byTier[t])}`);
  }
  if (pos(row.teamCents)) parts.push(`Team ${fmtUsd(row.teamCents)}`);
  if (pos(row.count)) parts.push(paymentsText(row.count));
  return parts.length ? parts.join(DOT) : null;
}

// One "Top recipients: Super Crits and Rumbles" row's detail line.
export function directDetailText(row) {
  if (!row || typeof row !== 'object') return null;
  const parts = [];
  if (pos(row.superCritCents)) parts.push(`Super Crits ${fmtUsd(row.superCritCents)}`);
  if (pos(row.rumbleCents)) parts.push(`Rumbles ${fmtUsd(row.rumbleCents)}`);
  return parts.length ? parts.join(DOT) : null;
}

// "Membership credits by tier": buckets with at least one payment, in TIER_ORDER.
export function tierRows(mem) {
  const byTier = mem && mem.byTier && typeof mem.byTier === 'object' ? mem.byTier : null;
  if (!byTier) return [];
  const out = [];
  for (const key of TIER_ORDER) {
    const b = byTier[key];
    if (!b || !pos(b.count)) continue;
    out.push({ key, label: TIER_LABELS[key], cents: isCents(b.cents) ? b.cents : 0, countText: paymentsText(b.count) });
  }
  return out;
}

// Memo lines M1-M9 under the net (section 5.15), in order.
export function membershipMemoLines(mem) {
  if (!mem || typeof mem !== 'object') return [];
  const out = [];
  const { revenueCents, creditedCents, feesEstCents, platformShareEstCents } = mem;
  if (isCents(revenueCents) && isCents(creditedCents) && isCents(feesEstCents) && isCents(platformShareEstCents)
    && (revenueCents !== 0 || creditedCents !== 0)) {
    out.push(`Membership platform share (est.): ${fmtUsd(platformShareEstCents)} = membership revenue ${fmtUsd(revenueCents)} ${MINUS} est. card fees ${fmtUsd(feesEstCents)} ${MINUS} membership credits ${fmtUsd(creditedCents)}`);
  }
  if (pos(mem.refundedCents) || pos(mem.disputedCents)) {
    out.push(`Membership refunds ${fmtUsd(isCents(mem.refundedCents) ? mem.refundedCents : 0)}, chargebacks ${fmtUsd(isCents(mem.disputedCents) ? mem.disputedCents : 0)}: CritCash absorbs these; recipients keep their credits`);
  }
  if (pos(mem.withheldCents)) out.push(`Membership credits withheld for review: ${fmtUsd(mem.withheldCents)} (not in the credits line)`);
  if (pos(mem.notPaidCents)) out.push(`Membership credits not paid, recipient not eligible: ${fmtUsd(mem.notPaidCents)} (CritCash keeps this)`);
  if (pos(mem.heldForReviewCents)) out.push(`Membership credits on a longer review hold: ${fmtUsd(mem.heldForReviewCents)} (still owed, in the credits line)`);
  if (pos(mem.nonCreditableCents)) out.push(`Paid membership months with no credit: ${fmtUsd(mem.nonCreditableCents)} (kept until refunded or replayed with a credit override)`);
  if (pos(mem.balanceFundedCents)) out.push(`Membership credits paid from members' Stripe credit balance: ${fmtUsd(mem.balanceFundedCents)} (booked earlier as Plan revenue, so credits can exceed membership revenue)`);
  if (pos(mem.extraLineCents)) out.push(`Other invoice lines inside membership revenue: ${fmtUsd(mem.extraLineCents)} (reclassify by hand)`);
  if (pos(revenueCents)) out.push('Stripe Billing and dispute fees are not in these estimates; book them in expenses.');
  return out;
}

// Memo lines D1-D3 under the net (section 5.15), in order.
export function directMemoLines(d) {
  if (!d || typeof d !== 'object') return [];
  const out = [];
  if (pos(d.notPaidCents)) out.push(`Super Crits and Rumbles not paid, recipient not eligible: ${fmtUsd(d.notPaidCents)} (CritCash keeps this)`);
  if (pos(d.heldForReviewCents)) out.push(`Super Crit and Rumble credits on a longer review hold: ${fmtUsd(d.heldForReviewCents)} (still owed, in their credit lines)`);
  if (pos(d.withheldCents)) out.push(`Super Crit and Rumble credits withheld for review: ${fmtUsd(d.withheldCents)} (not in their credit lines)`);
  return out;
}

// Year only: the non-creditable memo read failed or was truncated
// (yearTotals.membershipMemoError, null | string).
export function memoErrorLines(yt) {
  const e = yt && typeof yt === 'object' ? yt.membershipMemoError : null;
  if (e == null || e === false || e === '') return [];
  return ['Memo lines incomplete: paid membership months with no credit could not be read.'];
}

// "1 creator credited" / "2 creators credited"; noun 'creator' | 'recipient'.
export function creditedCountText(n, noun) {
  const k = isCents(n) ? n : 0;
  return `${plural(k, noun, `${noun}s`)} credited`;
}
